import type { ReactNode } from 'react';

/**
 * The editor's note a curated problem may carry (json_data->'Editor'), as it arrives from the
 * server: on a new line after the expert's commentary, under this side header — or alone, where
 * the problem has no commentary (client request, 2026-09-18).
 *
 * The server composes the text (`HawkesBridge.Core.Utilities.EditorNote`) so every surface that
 * shows the commentary carries the note without a contract change; what is left for the client is
 * the part plain text cannot do — putting the header in bold. The two must agree on the header.
 */
export const EDITOR_NOTE_HEADER = "Editor's note:";

/** The header, only where it STARTS a line — the same words mid-sentence are the expert's own. */
const HEADER_AT_LINE_START = /(^|\n)Editor's note:[ \t]*/;

/**
 * Splits commentary into the expert's words and the editor's note. `note` is null when the text
 * carries none; `comment` is empty when the note stands alone.
 */
export function splitEditorNote(text?: string | null): { comment: string; note: string | null } {
  if (!text) return { comment: '', note: null };

  const match = HEADER_AT_LINE_START.exec(text);
  if (!match) return { comment: text, note: null };

  return {
    comment: text.slice(0, match.index),
    note: text.slice(match.index + match[0].length),
  };
}

/**
 * Commentary ready to render as the children of a paragraph: the expert's words, then the note on
 * a line of its own with its header in bold. Text with no note comes back exactly as `renderText`
 * leaves it, so a caller can use this unconditionally.
 *
 * `renderText` is how the caller already renders its prose — the conceptual answer passes
 * `renderCallTokens` so a call inside the note reads as notation like the answer above it.
 */
export function renderWithEditorNote(
  text?: string | null,
  renderText: (value: string) => ReactNode = value => value
): ReactNode {
  const { comment, note } = splitEditorNote(text);
  if (note === null) return renderText(comment);

  return (
    <>
      {comment && renderText(comment)}
      {/* `block` gives the new line whatever the paragraph's white-space rule is. A span, not a
          div, because this renders inside a <p>. */}
      <span className="block" data-testid="editor-note">
        <strong className="font-bold" data-testid="editor-note-header">
          {EDITOR_NOTE_HEADER}
        </strong>{' '}
        {renderText(note)}
      </span>
    </>
  );
}
